import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import AIChat from './AIChat';
import {
  LayoutDashboard, Building2, Users, TrendingUp, Calendar,
  CheckSquare, Settings, Shield, Menu, X, LogOut, Moon, Sun,
  ChevronLeft, Bell, User, ShoppingCart, FileText, BarChart2, FileDown, UserCog, Package,
  Eye, EyeOff, Globe, Mail, Target, Warehouse, Factory, Box, ClipboardList, Boxes
} from 'lucide-react';
import SetupWizard from './SetupWizard';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

// module key maps to permission module
const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard',  module: 'dashboard' },
  { to: '/companies', icon: Building2,       label: 'Aziende',    module: 'companies' },
  { to: '/contacts',  icon: Users,           label: 'Contatti',   module: 'contacts' },
  { to: '/pipeline',  icon: TrendingUp,      label: 'Pipeline',   module: 'pipeline' },
  { to: '/calendar',  icon: Calendar,        label: 'Calendario', module: 'calendar' },
  { to: '/activities',icon: CheckSquare,     label: 'Attività',   module: 'activities' },
  { to: '/orders',    icon: ShoppingCart, label: 'Ordini',         module: 'orders' },
  { to: '/raccolta',  icon: ClipboardList,label: 'Foglio Raccolta',module: 'orders' },
  { to: '/products',  icon: Package,      label: 'Articoli',       module: 'orders' },
  { to: '/packaging', icon: Box,          label: 'Imballaggi', module: 'orders' },
  { to: '/product-stock', icon: Boxes,    label: 'Scorte Prodotti', module: 'orders' },
  { to: '/invoices',  icon: FileText,     label: 'Fatture',    module: 'invoices' },
  { to: '/forecast',   icon: Target,     label: 'Forecast',   module: null },
  { to: '/scorte',     icon: Warehouse,  label: 'Scorte',     module: 'inventory' },
  { to: '/produzione', icon: Factory,    label: 'Produzione', module: 'production' },
  { to: '/analytics',  icon: BarChart2,  label: 'Analisi',    module: 'analytics' },
  { to: '/reports',    icon: FileDown,   label: 'Report',     module: 'reports' },
];

const ADMIN_ITEMS = [
  { to: '/invitations', icon: Mail,    label: 'Inviti',          roles: ['admin', 'super_admin'] },
  { to: '/roles',       icon: UserCog, label: 'Ruoli e Permessi',roles: ['admin', 'super_admin'] },
  { to: '/settings',    icon: Settings,label: 'Impostazioni',    roles: ['admin', 'super_admin'] },
  { to: '/superadmin',  icon: Shield,  label: 'Super Admin',     roles: ['super_admin'] },
];

function useTheme() {
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem('agri_theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('agri_theme', dark ? 'dark' : 'light');
  }, [dark]);

  return { dark, toggle: () => setDark(d => !d) };
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { canView, scope } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { dark, toggle } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  // Check setup wizard for admin users
  useEffect(() => {
    if (user?.role !== 'admin') return;
    if (sessionStorage.getItem('wizard_dismissed')) return;
    import('axios').then(({ default: axios }) => {
      import('../contexts/AuthContext').then(({ API }) => {
        axios.get(`${API}/admin/setup-status`)
          .then(res => { if (!res.data.setup_completed) setShowWizard(true); })
          .catch(() => {});
      });
    });
  }, [user]);

  const SCOPE_CONFIG = {
    own:  { label: 'Solo propri', className: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50', Icon: EyeOff },
    role: { label: 'Vista team',  className: 'text-blue-600  dark:text-blue-400  bg-blue-50  dark:bg-blue-900/20  border border-blue-200  dark:border-blue-700/50',   Icon: Eye },
    all:  { label: 'Globale',     className: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/50', Icon: Globe },
  };
  const scopeCfg = SCOPE_CONFIG[scope] || SCOPE_CONFIG.all;
  const showScopeBadge = !['admin', 'super_admin'].includes(user?.role);

  // Filter nav items by permission
  const visibleNavItems = NAV_ITEMS.filter(item => !item.module || canView(item.module));

  // Compute nav class as string (avoid function className — Radix Slot stringifies it)
  const getNavClass = (to) => {
    const isActive = to === '/dashboard'
      ? location.pathname === '/dashboard'
      : location.pathname.startsWith(to);
    return `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
      isActive
        ? 'bg-primary text-primary-foreground shadow-sm'
        : 'text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700/60 hover:text-slate-900 dark:hover:text-white'
    } ${collapsed ? 'justify-center' : ''}`;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user?.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

  const roleLabel = {
    super_admin: 'Super Admin',
    admin: 'Amministratore',
    sales_manager: 'Resp. Commerciale',
    sales_rep: 'Commerciale',
    operator: 'Operatore',
    client: 'Cliente',
  }[user?.role] || user?.role;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-slate-200 dark:border-slate-700/50 ${collapsed ? 'justify-center' : ''}`}>
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
          <span className="text-primary-foreground font-bold text-sm font-mono">A</span>
        </div>
        {!collapsed && (
          <div>
            <p className="font-bold text-sm text-slate-800 dark:text-slate-100" style={{fontFamily:'Manrope, sans-serif'}}>AGRI-CRM</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{user?.tenant_name || 'CRM Platform'}</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
        <TooltipProvider>
          {visibleNavItems.map(({ to, icon: Icon, label }) => (
            <Tooltip key={to} delayDuration={0}>
              <TooltipTrigger asChild>
                <NavLink
                  to={to}
                  data-testid={`nav-${label.toLowerCase()}`}
                  onClick={() => setMobileOpen(false)}
                  className={getNavClass(to)}
                >
                  <Icon size={18} strokeWidth={2} className="flex-shrink-0" />
                  {!collapsed && <span>{label}</span>}
                </NavLink>
              </TooltipTrigger>
              {collapsed && <TooltipContent side="right"><p>{label}</p></TooltipContent>}
            </Tooltip>
          ))}

          {/* Admin section */}
          {ADMIN_ITEMS.filter(item => item.roles.includes(user?.role)).length > 0 && (
            <>
              {!collapsed && <p className="text-xs uppercase font-medium text-slate-500 dark:text-slate-400 px-3 pt-4 pb-1 tracking-wider">Admin</p>}
              {collapsed && <div className="border-t border-border mx-2 my-2" />}
              {ADMIN_ITEMS.filter(item => item.roles.includes(user?.role)).map(({ to, icon: Icon, label }) => (
                <Tooltip key={to} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={to}
                      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setMobileOpen(false)}
                      className={getNavClass(to)}
                    >
                      <Icon size={18} strokeWidth={2} className="flex-shrink-0" />
                      {!collapsed && <span>{label}</span>}
                    </NavLink>
                  </TooltipTrigger>
                  {collapsed && <TooltipContent side="right"><p>{label}</p></TooltipContent>}
                </Tooltip>
              ))}
            </>
          )}
        </TooltipProvider>
      </nav>

      {/* User footer */}
      <div className={`border-t border-slate-200 dark:border-slate-700/50 p-3 ${collapsed ? 'flex justify-center' : ''}`}>
        {collapsed ? (
          <Avatar className="w-8 h-8 cursor-pointer" onClick={handleLogout}>
            <AvatarFallback className="text-xs bg-primary text-primary-foreground">{initials}</AvatarFallback>
          </Avatar>
        ) : (
          <div className="flex items-center gap-2">
            <Avatar className="w-8 h-8">
              <AvatarFallback className="text-xs bg-primary text-primary-foreground">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-slate-800 dark:text-slate-100">{user?.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{roleLabel}</p>
              {showScopeBadge && (
                <span className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${scopeCfg.className}`} data-testid="scope-badge-sidebar">
                  <scopeCfg.Icon size={9} strokeWidth={2.5} />
                  {scopeCfg.label}
                </span>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" className="h-8 w-8 flex-shrink-0 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white">
              <LogOut size={14} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex flex-col border-r border-slate-200 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-900 transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700/50 flex flex-col">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="glass sticky top-0 z-20 border-b border-border bg-card/80 backdrop-blur-md">
          <div className="flex items-center gap-3 px-4 h-14">
            {/* Mobile menu */}
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)}>
              <Menu size={18} />
            </Button>

            {/* Desktop collapse toggle */}
            <Button
              variant="ghost" size="icon"
              className="hidden md:flex"
              onClick={() => setCollapsed(c => !c)}
              data-testid="sidebar-toggle"
            >
              {collapsed ? <Menu size={16} /> : <ChevronLeft size={16} />}
            </Button>

            <div className="flex-1" />

            {/* Theme toggle */}
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" className="h-8 w-8">
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </Button>

            {/* User badge */}
            <div className="flex items-center gap-2">
              {showScopeBadge && (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <span
                        className={`hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium cursor-default ${scopeCfg.className}`}
                        data-testid="scope-badge-header"
                      >
                        <scopeCfg.Icon size={11} strokeWidth={2.5} />
                        {scopeCfg.label}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      Vista dati attiva per il tuo ruolo: <strong>{scopeCfg.label}</strong>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Badge variant="outline" className="hidden sm:flex text-xs">
                {roleLabel}
              </Badge>
              <Avatar className="w-8 h-8">
                <AvatarFallback className="text-xs bg-primary text-primary-foreground">{initials}</AvatarFallback>
              </Avatar>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-6 min-h-full">
            <Outlet />
          </div>
        </main>
      </div>

      {/* AI Chat */}
      <AIChat />

      {/* Setup Wizard */}
      {showWizard && (
        <SetupWizard
          tenantName={user?.tenant_name || 'la tua azienda'}
          onClose={() => { setShowWizard(false); sessionStorage.setItem('wizard_dismissed', '1'); }}
        />
      )}
    </div>
  );
}
